import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import QTDetailsTextFieldWithLabelSection from "../../../../common/QTDetailsTextFieldWithLabelSection";
import { db } from "../../../../firebase";
import { toTitleCaseArea } from "../../../../constants";

// ─── Shared MUI sx for the fieldset-style outlined fields ───
const areasFieldsetSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 14,
    "& fieldset": {
      borderColor: "#C4C4C4",
    },
    "&:hover fieldset": {
      borderColor: "#00A7CD",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#00A7CD",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 14,
    color: "#666",
    "&.Mui-focused": {
      color: "#00A7CD",
    },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#000000",
  },
};

const RESERVED_AREA_NAMES = new Set(["all", "nearby"]);

const isReservedArea = (name) =>
  RESERVED_AREA_NAMES.has(String(name || "").trim().toLowerCase());

const getNextPosition = (albumsAndQTs = []) => {
  const positions = (albumsAndQTs || [])
    .map((x) => (typeof x?.position === "number" ? x.position : null))
    .filter((x) => x != null);
  if (positions.length === 0) return (albumsAndQTs?.length || 0) + 1;
  return Math.max(...positions) + 1;
};

/**
 * areasMode:
 * - "none": don't render
 * - "batchSingle": display / edit single area using quicktaleDetails.areaIdResolved
 * - "orgMulti": display / edit multi areas determined by area.albumsAndQTs membership
 */
const QTAreasField = ({
  areasMode = "none",
  quicktaleDetails,
  setQuicktaleDetails,
  editEnabled,
}) => {
  const [areas, setAreas] = useState([]);
  const initialAreaIdsRef = useRef(null);

  const orgId = quicktaleDetails?.organization;
  const qtId = quicktaleDetails?.id;

  // Subscribe to org areas when needed
  useEffect(() => {
    if (areasMode === "none") return;
    if (!orgId) {
      setAreas([]);
      return;
    }

    const q = query(
      collection(db, "area"),
      where("organization", "==", orgId),
      orderBy("position")
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAreas(data);
      },
      () => setAreas([])
    );

    return unsub;
  }, [areasMode, orgId]);

  const selectableAreas = useMemo(
    () => (areas || []).filter((a) => !isReservedArea(a?.name)),
    [areas]
  );

  // Compute multi-area membership for orgMulti
  const associatedAreas = useMemo(() => {
    if (areasMode !== "orgMulti") return [];
    if (!qtId) return [];

    return (areas || []).filter((a) => {
      const list = a?.albumsAndQTs || [];
      return list.some((x) => x?.type === "quicktales" && x?.id === qtId);
    });
  }, [areasMode, areas, qtId]);

  // Sync computed associated areas into quicktaleDetails.uiAreaIds in view+edit modes.
  // Only initialize initialAreaIdsRef once per qtId.
  useEffect(() => {
    if (areasMode !== "orgMulti") return;
    if (!qtId) return;

    const ids = associatedAreas.map((a) => a.id);

    // Initialize baseline for diffing (first time we can compute areas).
    if (initialAreaIdsRef.current == null) {
      initialAreaIdsRef.current = ids;
    }

    // Keep UI state in quicktaleDetails so container can persist on save.
    setQuicktaleDetails((prev) => {
      const prevIds = Array.isArray(prev?.uiAreaIds) ? prev.uiAreaIds : null;
      // If user is editing, do not overwrite their selections.
      if (editEnabled && Array.isArray(prevIds)) return prev;
      return { ...prev, uiAreaIds: ids };
    });
  }, [areasMode, qtId, associatedAreas, setQuicktaleDetails, editEnabled]);

  if (areasMode === "none") return null;

  if (areasMode === "batchSingle") {
    const areaId = quicktaleDetails?.areaIdResolved;
    const resolvedName = (() => {
      if (areaId) {
        const found = areas.find((a) => a.id === areaId);
        return found?.name || quicktaleDetails?.areaNameFromCsv || "";
      }
      return quicktaleDetails?.areaNameFromCsv || "";
    })();

    return (
      <Grid container direction={"column"} sx={{ mt: 1 }}>
        <QTDetailsTextFieldWithLabelSection
          label={"Area"}
          value={resolvedName}
          disabled={true}
          editEnabled={editEnabled}
          select={editEnabled}
          noTextTransform
          dropdownValues={["", ...selectableAreas.map((a) => a.name)]}
          onChange={(e) => {
            const rawValue = e.target.value;
            // If the user typed a value that doesn't match an existing area, apply title case
            const selectedArea = selectableAreas.find((a) => a.name === rawValue);
            const selectedAreaName = selectedArea ? rawValue : toTitleCaseArea(rawValue);
            setQuicktaleDetails((prev) => ({
              ...prev,
              areaIdResolved: selectedArea?.id || "",
              areaNameFromCsv: selectedAreaName || "",
            }));
          }}
        />
      </Grid>
    );
  }

  // orgMulti
  const uiAreaIds = Array.isArray(quicktaleDetails?.uiAreaIds)
    ? quicktaleDetails.uiAreaIds
    : [];

  const uiAreas = uiAreaIds
    .map((id) => areas.find((a) => a.id === id))
    .filter(Boolean);

  const availableToAdd = selectableAreas.filter(
    (a) => !uiAreaIds.includes(a.id)
  );

  if (!editEnabled) {
    // In view mode, prefer the freshly computed membership (associatedAreas)
    // so the chips remain stable even if the parent replaces quicktaleDetails
    // (and drops uiAreaIds) on re-renders. Fall back to uiAreas otherwise.
    const viewAreas = associatedAreas.length > 0 ? associatedAreas : uiAreas;
    return (
      <Grid container alignItems={"flex-start"} style={{ marginBottom: 24 }}>
        <span style={labelStyle}>Areas</span>
        <Box sx={{ width: "70%" }}>
          {viewAreas.length > 0 ? (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {viewAreas.map((a) => (
                <Chip key={a.id} label={a.name} size="small" />
              ))}
            </Box>
          ) : (

            <Typography
              sx={{
                fontSize: 14,
                fontFamily: "Avenir Book, sans-serif",
                color: "#666",
              }}
            >
              No areas assigned
            </Typography>
          )}
        </Box>
      </Grid>
    );
  }


  return (
    <Grid container style={{ marginBottom: 24 }}>
      <span style={labelStyle}>Areas</span>
      <Box sx={{ width: "70%" }}>
        {/* Selected area chips above the field */}
        {uiAreas.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {uiAreas.map((a) => (
              <Chip
                key={a.id}
                label={a.name}
                size="small"
                onDelete={() => {
                  setQuicktaleDetails((prev) => ({
                    ...prev,
                    uiAreaIds: (prev?.uiAreaIds || []).filter((x) => x !== a.id),
                  }));
                }}
              />
            ))}
          </Box>
        )}

        <TextField
          label="Areas"
          variant="outlined"
          fullWidth
          select
          value=""
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: () => (
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: "Avenir Book, sans-serif",
                  color: "#666",
                }}
              >
                Add area
              </Typography>
            ),
          }}
          onChange={(e) => {
            const idToAdd = e.target.value;
            if (!idToAdd) return;
            setQuicktaleDetails((prev) => {
              const prevIds = Array.isArray(prev?.uiAreaIds)
                ? prev.uiAreaIds
                : [];
              if (prevIds.includes(idToAdd)) return prev;
              return { ...prev, uiAreaIds: [...prevIds, idToAdd] };
            });
          }}
          sx={areasFieldsetSx}
        >
          {availableToAdd.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {a.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Grid>
  );
};

export default QTAreasField;

const labelStyle = {
  width: "25%",
  color: "#020303",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};

export const getAreaMembershipDiff = ({ initialAreaIds, currentAreaIds }) => {
  const initial = new Set(initialAreaIds || []);
  const current = new Set(currentAreaIds || []);

  const added = [];
  const removed = [];

  for (const id of current) {
    if (!initial.has(id)) added.push(id);
  }
  for (const id of initial) {
    if (!current.has(id)) removed.push(id);
  }

  return { added, removed };
};

export const upsertQuickTaleInAlbumsAndQTs = ({ albumsAndQTs = [], qtId }) => {
  if (!qtId) return albumsAndQTs || [];
  const exists = (albumsAndQTs || []).some(
    (x) => x?.type === "quicktales" && x?.id === qtId
  );
  if (exists) return albumsAndQTs || [];
  const nextPosition = getNextPosition(albumsAndQTs);
  return [
    ...(albumsAndQTs || []),
    { id: qtId, type: "quicktales", position: nextPosition },
  ];
};

export const removeQuickTaleFromAlbumsAndQTs = ({ albumsAndQTs = [], qtId }) => {
  if (!qtId) return albumsAndQTs || [];
  return (albumsAndQTs || []).filter(
    (x) => !(x?.type === "quicktales" && x?.id === qtId)
  );
};
